import { createSlice } from '@reduxjs/toolkit';
import { ExecutionStatus } from '@dynamika-shared/types/executions';
import {
  QCTChangeReadingLabelAction,
  QCTReadingsFetchAction,
  QCTReadingsState,
  QCTReadingsSyncAction,
  QCTReadingsSyncStatusAction,
  QCTRemoveSelectedReadingAction,
  QCTSendSelectedReadingAction,
  QCTVisitDataFetchSuccessAction,
  QCTWorkstationsDataFetchSuccessAction,
} from './QCTReadings.types';
import { createSelector, createSliceHook, makeActionCreator } from '../helpers';

const initialState: QCTReadingsState = {
  loadingReadings: false,
  loadingWorkstationReadings: false,
  error: undefined,
  visitData: null,
  workstationData: [],
  lastSync: '',
  syncStatus: ExecutionStatus.COMPLETED,
};

const { act, actReq, actResolve, actReject, actEmpty } =
  makeActionCreator<QCTReadingsState>();

export const QCTReadingsSlice = createSlice({
  name: 'QCTReadings',
  initialState,
  reducers: {
    fetchQCTReadings:
      actReq<QCTReadingsFetchAction['payload']>('loadingReadings'),
    fetchQCTReadingsSuccess: actResolve<
      QCTVisitDataFetchSuccessAction['payload']
    >('visitData', 'loadingReadings'),
    fetchQCTReadingsFailure: actReject('error', 'loadingReadings'),

    fetchQCTWorkstationReadings: actReq<QCTReadingsFetchAction['payload']>(
      'loadingWorkstationReadings'
    ),
    fetchQCTWorkstationReadingsSuccess: actResolve<
      QCTWorkstationsDataFetchSuccessAction['payload']
    >('workstationData', 'loadingWorkstationReadings'),
    fetchQCTWorkstationReadingsFailure: actReject(
      'error',
      'loadingWorkstationReadings'
    ),

    syncWorkstation: act<QCTReadingsSyncAction['payload']>(() => ({
      syncStatus: ExecutionStatus.IN_PROGRESS,
    })),
    syncWorkstationStatus: act<QCTReadingsSyncStatusAction['payload']>(
      ({ lastSync, status }) => ({
        syncStatus: status,
        lastSync,
      })
    ),

    removeSelectedReading:
      actEmpty<QCTRemoveSelectedReadingAction['payload']>(),
    changeReadingLabel: actEmpty<QCTChangeReadingLabelAction['payload']>(),
    sendToReadings: actEmpty<QCTSendSelectedReadingAction['payload']>(),
    getSyncStatus: actEmpty<QCTReadingsSyncAction['payload']>(),
    /** Dispatched when the QCT page unmounts, to stop the 30s poll loop. */
    stopSyncPolling: actEmpty(),
  },
});

export const {
  fetchQCTReadings,
  fetchQCTReadingsSuccess,
  fetchQCTReadingsFailure,
  fetchQCTWorkstationReadings,
  fetchQCTWorkstationReadingsSuccess,
  fetchQCTWorkstationReadingsFailure,
  removeSelectedReading,
  changeReadingLabel,
  sendToReadings,
  getSyncStatus,
  stopSyncPolling,
  syncWorkstation,
  syncWorkstationStatus,
} = QCTReadingsSlice.actions;

export const actions = {
  ...QCTReadingsSlice.actions,
};

export const useQCTReadingsSlice = createSliceHook(
  QCTReadingsSlice.name,
  actions
);

export const selectQCTReadingsState = createSelector('QCTReadings');
